<!-- The choice itself is the card: one of the two ladders is always selected, and every PO submits
     one. There is no third "off" state -- the first preset renders exactly what the template
     renders with no encoding_ladder key at all, so an explicit selection costs nothing and is
     honest about what the job will do. "Customize" is a disclosure over that selection, not a mode. -->
<div class="row">
  <div class="col-12">
    <div class="card cl-tight">
      <div class="card-header">Encoding ladder</div>
      <div class="card-body">

        <!-- Radios rather than a dropdown: there are two, they are the decision this card exists
             for, and each needs a sentence of explanation a dropdown option cannot carry. -->
        <div class="row cl-spacing-small">
          @for (p of presets; track p.id) {
            <div class="col-12 col-md-6">
              <div class="form-check cl-spacing-none">
                <input
                  (change)="applyPreset(p.id)"
                  [checked]="p.id === presetId"
                  [disabled]="form.disabled"
                  class="form-check-input"
                  id="preset_{{p.id}}"
                  name="ladder_preset"
                  type="radio">
                <label class="form-check-label" for="preset_{{p.id}}">{{ p.label }}</label>
              </div>
              <p class="valid-feedback">{{ p.description }}</p>
            </div>
          }
        </div>

        @if (presetId === customPresetId) {
          <p class="valid-feedback">
            Edited by hand — neither preset any more. Pick one above to start over from a known ladder;
            it replaces everything below.
          </p>
        }

        <div class="cl-spacing-box">
          <button (click)="toggleCustomize()" class="btn cl-color-secondary" type="button">
            {{ expanded ? 'Hide settings' : 'Customize' }}
          </button>
        </div>

        @if (expanded) {
          <div [formGroup]="form">

    <div class="row cl-spacing-small mt-2">
      <div class="col-12">
        <div class="form-check">
          <input class="form-check-input" formControlName="exceed_input_resolution" id="exceed_input_resolution" type="checkbox">
          <label class="form-check-label" for="exceed_input_resolution">Upscale beyond the source resolution</label>
        </div>
        <p class="valid-feedback">Off drops any rendition wider than the source rather than upscaling to it.</p>
      </div>
    </div>

    <!-- Both settings are ladder-level, not per codec group: the packager takes a single segment
         duration for the whole job, so a per-group GOP could not be honoured, and a segment that is
         not a whole number of GOPs would not start on a keyframe. -->
    <div class="row cl-spacing-small mt-2">
      <div class="col-3">
        <label class="form-label" for="gop_in_seconds">GOP (seconds)</label>
        <input
          [class.invalid]="form.get('gop_in_seconds').errors && form.get('gop_in_seconds').touched"
          class="form-control" formControlName="gop_in_seconds" id="gop_in_seconds" min="1" step="1" type="number">
        <p class="valid-feedback">Closed-GOP length. Keyframes align within a group, and every group cuts at the same frames.</p>
      </div>
      <div class="col-3">
        <label class="form-label" for="gops_per_segment">GOPs per segment</label>
        <input
          [class.invalid]="form.get('gops_per_segment').errors && form.get('gops_per_segment').touched"
          class="form-control" formControlName="gops_per_segment" id="gops_per_segment" min="1" step="1" type="number">
        <p class="valid-feedback">A segment has to start on a keyframe, so this is a whole number of GOPs.</p>
      </div>
      <div class="col-6 align-self-center">
        @if (segmentDuration() !== null) {
          <p class="valid-feedback mb-0">Segment duration: <b>{{ segmentDuration() }} s</b> — what the packager is handed.</p>
        }
      </div>
    </div>

    <!-- Switching groups: a player picks one and switches only within its renditions, which is why
         keyframe alignment and codec settings are per group and never across them.

         Every list below tracks the *control instance*, not $index. With $index, removing a row
         other than the last one made the survivors render the wrong values: Angular reused the
         row's DOM, and because `[formGroupName]="i"` kept the same value the directive never
         re-resolved, so the surviving row went on showing the control that had just been removed --
         which reads as "it deleted the last one instead".

         Identity tracking makes Angular log NG0956 when a preset is applied, because _load really
         does replace every control. That warning is the honest cost of the fix and fires on nothing
         else: not on edits, not on removals. Do not quiet it by going back to $index. -->
    <div formArrayName="groups">
      @for (group of groups.controls; track group; let gi = $index) {
        <div [formGroupName]="gi" class="card cl-color-haze mt-3">
          <div class="card-body">

            <div class="row cl-spacing-small align-items-end">
              <div class="col-4">
                <label class="form-label" for="codec_{{gi}}">Codec</label>
                <select class="form-select" formControlName="codec" id="codec_{{gi}}">
                  @for (c of availableCodecs(gi); track c) {
                    <option [ngValue]="c">{{ codecs[c].label }} ({{ codecs[c].encoder }})</option>
                  }
                </select>
              </div>
              <div class="col-3">
                <label class="form-label">Outputs</label>
                <div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" formControlName="out_dash" id="out_dash_{{gi}}" type="checkbox">
                    <label class="form-check-label" for="out_dash_{{gi}}">DASH</label>
                  </div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" formControlName="out_hls" id="out_hls_{{gi}}" type="checkbox">
                    <label class="form-check-label" for="out_hls_{{gi}}">HLS</label>
                  </div>
                </div>
              </div>
              <div class="col-2">
                <label class="form-label">Rate control</label>
                <div class="form-check">
                  <input class="form-check-input" formControlName="two_pass" id="two_pass_{{gi}}" type="checkbox">
                  <label class="form-check-label" for="two_pass_{{gi}}">Two-pass VBR</label>
                </div>
              </div>
              <div class="col-2">
                <label class="form-label" for="crf_{{gi}}">CRF</label>
                <!-- Disabled, not readonly, when two-pass is on: the value is not sent at all, and a
                     readonly input paints exactly like a live one. -->
                <input
                  [class.invalid]="group.get('crf').errors && group.get('crf').touched"
                  class="form-control"
                  formControlName="crf"
                  id="crf_{{gi}}"
                  max="51"
                  min="0"
                  step="1"
                  type="number">
              </div>
              <div class="col-1 text-end">
                @if (groups.length > 1) {
                  <button (click)="removeGroup(gi)" aria-label="Remove codec group" class="cl-btn-quickaction" type="button">✕</button>
                }
              </div>
            </div>

            <div class="row cl-spacing-small mt-2">
              <div class="col-3">
                <label class="form-label" for="profile_{{gi}}">Profile</label>
                <select class="form-select" formControlName="profile" id="profile_{{gi}}">
                  <option [ngValue]="null">Codec default</option>
                  @for (p of profilesFor(group); track p) {
                    <option [ngValue]="p">{{ p }}</option>
                  }
                </select>
              </div>
              <div class="col-3">
                <label class="form-label" for="preset_{{gi}}">Preset</label>
                <select class="form-select" formControlName="preset" id="preset_{{gi}}">
                  <option [ngValue]="null">Codec default</option>
                  @for (p of presetsFor(group); track p) {
                    <option [ngValue]="p">{{ p }}</option>
                  }
                </select>
              </div>
              <div class="col-3">
                <label class="form-label" for="pixfmt_{{gi}}">Pixel format</label>
                <select class="form-select" formControlName="pixfmt" id="pixfmt_{{gi}}">
                  @for (p of pixelFormats; track p) {
                    <option [ngValue]="p">{{ p }}</option>
                  }
                </select>
              </div>
              <div class="col-3 align-self-end">
                @if (!supportsTwoPass(group)) {
                  <p class="valid-feedback">
                    SVT-AV1 cannot target an average under a peak cap, so AV1 stays on capped CRF.
                  </p>
                }
              </div>
            </div>

            @if (group.errors?.noOutputs) {
              <p class="invalid-feedback d-block">Pick at least one output — DASH, HLS, or both.</p>
            }
            @if (group.errors?.twoPassUnsupported) {
              <p class="invalid-feedback d-block">
                {{ codecs[group.errors.twoPassUnsupported].label }} cannot do two-pass VBR. Leave it off.
              </p>
            }
            @if (group.errors?.profileTooShallow) {
              <p class="invalid-feedback d-block">
                {{ group.errors.profileTooShallow }} — the encoder refuses to open on that pair rather
                than falling back, so pick a deeper profile or an 8-bit pixel format.
              </p>
            }
            @if (group.get('crf').errors?.required) {
              <p class="invalid-feedback d-block">
                A single-pass group must state its constant-quality target (CRF), or switch to two-pass
                and give every rendition an average bitrate.
              </p>
            }

            <!-- Renditions: width also decides which DRM tier's key a rendition is encrypted with, and
                 max_bitrate keys the output filename. -->
            <div class="row cl-spacing-small mt-3">
              <div class="col-12">
                <label class="form-label">Renditions</label>
              </div>
            </div>
            <div class="row g-2">
              <div class="col-2"><label class="form-label mb-1">Width</label></div>
              <div class="col-2"><label class="form-label mb-1">Avg bitrate</label></div>
              <div class="col-2"><label class="form-label mb-1">Max bitrate</label></div>
              <div class="col-2"><label class="form-label mb-1">Min bitrate</label></div>
              <div class="col-2"><label class="form-label mb-1">Bufsize</label></div>
              <div class="col-2"></div>
            </div>
            <div formArrayName="renditions">
              @for (rendition of renditionsOf(group).controls; track rendition; let ri = $index) {
                <div [formGroupName]="ri" class="row g-2 align-items-end mb-1">
                  <div class="col-2">
                    <input [class.invalid]="rendition.get('width').errors" class="form-control" formControlName="width" min="1" step="1" type="number">
                  </div>
                  <div class="col-2">
                    <input [class.invalid]="rendition.get('avg_bitrate').errors" class="form-control" formControlName="avg_bitrate" placeholder="—" type="text">
                  </div>
                  <div class="col-2">
                    <input [class.invalid]="rendition.get('max_bitrate').errors" class="form-control" formControlName="max_bitrate" placeholder="2200k" type="text">
                  </div>
                  <div class="col-2">
                    <input [class.invalid]="rendition.get('min_bitrate').errors" class="form-control" formControlName="min_bitrate" placeholder="—" type="text">
                  </div>
                  <div class="col-2">
                    <input [class.invalid]="rendition.get('bufsize').errors" class="form-control" formControlName="bufsize" placeholder="—" type="text">
                  </div>
                  <div class="col-2 text-end">
                    <button
                      (click)="removeRendition(group, ri)"
                      [disabled]="renditionsOf(group).length < 2"
                      aria-label="Remove rendition"
                      class="cl-btn-quickaction"
                      type="button">✕
                    </button>
                  </div>
                </div>
              }
            </div>
            @if (renditionsOf(group).errors?.noRenditions) {
              <p class="invalid-feedback d-block">A group needs at least one rendition.</p>
            }
            @if (group.get('two_pass').value) {
              <p class="valid-feedback">Two-pass targets each rendition's average bitrate under its max — none may be left blank.</p>
            }

            <button (click)="addRendition(group)" class="btn btn-sm cl-color-secondary mt-2" type="button">Add rendition</button>
          </div>
        </div>
      }
    </div>

    @if (groups.errors?.duplicateCodec) {
      <p class="invalid-feedback d-block">
        Two groups use codec "{{ groups.errors.duplicateCodec }}" — one group per codec.
      </p>
    }
    @if (groups.errors?.noGroups) {
      <p class="invalid-feedback d-block">The ladder needs at least one codec group.</p>
    }
    @if (canAddGroup()) {
      <button (click)="addGroup()" class="btn btn-sm cl-color-secondary mt-3" type="button">Add codec group</button>
    }

    <!-- Shared across every group: one audio set is referenced by all of them, and the DRM split
         is by width regardless of codec. -->
    <div class="row cl-spacing-small mt-4">
      <div class="col-6">
        <label class="form-label" for="audio_mode">Audio</label>
        <select (change)="setAudioMode($any($event.target).value)" class="form-select" id="audio_mode">
          @for (m of audioStartModes; track m.id) {
            <option [value]="m.id" [selected]="m.id === form.get('audio_mode').value">{{ m.label }}</option>
          }
          <option [value]="form.get('audio_mode').value" [selected]="isCustomAudio()" [disabled]="!isCustomAudio()">
            Custom
          </option>
        </select>
        <p class="valid-feedback">{{ audioModeDescription() }}</p>
      </div>
    </div>

    <!-- One table for all three modes. A row is one output rendition -- one channel layout in one codec
         -- which is already the shape of an audio_renditions entry and what an audio_layouts entry
         collapses to per codec. Columns a mode does not write are disabled rather than removed, so
         the table keeps one shape and it stays visible which knobs exist. -->
    <div class="row g-2">
      <div class="col-2"><label class="form-label mb-1">Channels</label></div>
      <div class="col-2"><label class="form-label mb-1">Codec</label></div>
      <div class="col-2"><label class="form-label mb-1">Bitrate</label></div>
      <div class="col-2"><label class="form-label mb-1">Sample rate</label></div>
      <div class="col-1"><label class="form-label mb-1">Menu name</label></div>
      <div class="col-2"><label class="form-label mb-1">Layout policy</label></div>
      <div class="col-1"></div>
    </div>
    <div formArrayName="audio_rows">
      @for (row of audioRows.controls; track row; let ri = $index) {
        <div [formGroupName]="ri" class="row g-2 align-items-end mb-1">
          <div class="col-2">
            <input
              [attr.disabled]="audioColumnApplies('channels') ? null : ''"
              [class.invalid]="row.get('channels').errors"
              [placeholder]="audioColumnApplies('channels') ? 'source' : 'source'"
              class="form-control" formControlName="channels" min="1" step="1" type="number">
          </div>
          <div class="col-2">
            <select
              [attr.disabled]="audioColumnApplies('codec') ? null : ''"
              class="form-select" formControlName="codec">
              @for (c of audioCodecsFor(row); track c) {
                <option [ngValue]="c">{{ codecLabels([c]) }}</option>
              }
            </select>
          </div>
          <div class="col-2">
            <input
              [attr.disabled]="audioColumnApplies('bitrate') ? null : ''"
              [class.invalid]="row.get('bitrate').errors"
              class="form-control" formControlName="bitrate" placeholder="source" type="text">
          </div>
          <div class="col-2">
            <input
              [attr.disabled]="audioColumnApplies('samplerate') ? null : ''"
              class="form-control" formControlName="samplerate" placeholder="source" step="1" type="number">
          </div>
          <div class="col-1">
            <input
              [attr.disabled]="audioColumnApplies('display') ? null : ''"
              class="form-control" formControlName="display" placeholder="none" type="text">
          </div>
          <div class="col-2">
            <div class="form-check form-check-inline">
              <input
                [attr.disabled]="audioColumnApplies('required') ? null : ''"
                class="form-check-input" formControlName="required" id="required_{{ri}}" type="checkbox">
              <label class="form-check-label" for="required_{{ri}}">Required</label>
            </div>
            <div class="form-check form-check-inline">
              <input
                [attr.disabled]="audioColumnApplies('allow_downmix') ? null : ''"
                class="form-check-input" formControlName="allow_downmix" id="downmix_{{ri}}" type="checkbox">
              <label class="form-check-label" for="downmix_{{ri}}">Accept downmix</label>
            </div>
          </div>
          <div class="col-1 text-end">
            @if (audioRows.length > 1) {
              <button
                (click)="removeAudioRow(ri)"
                [disabled]="audioRows.length < 2"
                aria-label="Remove audio rendition"
                class="cl-btn-quickaction"
                type="button">✕
              </button>
            }
          </div>
        </div>
      }
    </div>

    @if (audioRows.errors?.duplicatePair) {
      <p class="invalid-feedback d-block">
        Two rows deliver {{ audioRows.errors.duplicatePair }} — that pairing is one rendition, so it would
        be encoded twice into the same file.
      </p>
    }
    @if (audioRows.errors?.inconsistentLayout) {
      <p class="invalid-feedback d-block">
        The rows for {{ layoutName(audioRows.errors.inconsistentLayout) }} disagree on bitrate, menu name
        or delivery — they are one layout in several codecs, so those have to match.
      </p>
    }
    @if (audioRows.errors?.stereoOnlyCodec) {
      <p class="invalid-feedback d-block">
        {{ audioRows.errors.stereoOnlyCodec }} carries its second channel parametrically and is only
        defined for stereo — set that row to 2 channels, or pick another codec.
      </p>
    }
    @if (audioRows.errors?.noRequiredLayout) {
      <p class="invalid-feedback d-block">
        Mark one layout <b>Required</b>, or a source in a layout that is not listed ships with no audio
        the player is guaranteed to have.
      </p>
    }
    @if (audioRows.errors?.duplicateRendition) {
      <p class="invalid-feedback d-block">
        Two renditions agree on codec, channels and bitrate ("{{ audioRows.errors.duplicateRendition }}") —
        together those name the output file, so one would overwrite the other.
      </p>
    }
    @if (audioRows.errors?.noRows) {
      <p class="invalid-feedback d-block">Add at least one rendition.</p>
    }

    <p class="valid-feedback">
      @if (form.get('audio_mode').value === 'custom_renditions') {
        Every track gets one output per row, whatever layout it arrived in. Leave a field empty to keep
        the source value.
      } @else {
        One row per layout and codec. <b>Required</b> means the layout is delivered even when no source
        track is in it; <b>Accept downmix</b> allows it to be derived from a wider source. A required
        layout that cannot accept a downmix is only delivered when a source track already has it.
        Nothing is ever up-mixed.
        @if (audioRowsAreDefault()) {
          These are the template's own defaults, so the job stays on whatever it derives.
        } @else {
          Edited — the job carries this policy explicitly.
        }
      }
    </p>

    <button (click)="addAudioRow()" class="btn btn-sm cl-color-secondary mt-2" type="button">Add rendition</button>
    @if (!audioRowsAreDefault()) {
      <button (click)="resetAudioRows()" class="btn btn-sm cl-color-secondary mt-2 ms-2" type="button">
        Reset to defaults
      </button>
    }

    <div class="row cl-spacing-small mt-4">
      <div class="col-12">
        <label class="form-label">DRM key tiers</label>
        <p class="valid-feedback">
          Which DRM key a rendition is encrypted with, by width. Effectively global: the same content is
          reused across deliveries, so two jobs mapping a width onto different tiers would request
          different keys for the same rendition. Change this only if every job for the content agrees.
        </p>
      </div>
    </div>
    <div class="row g-2">
      <div class="col-4"><label class="form-label mb-1">Stream type</label></div>
      <div class="col-4"><label class="form-label mb-1">Max width (inclusive)</label></div>
      <div class="col-2"></div>
    </div>
    <!-- Shown, not offered: audio is keyed separately from video and the key is requested for
         every job, so there is nothing to decide. It is in the submitted ladder all the same --
         the stored PO is the only record of the key set a job ran with. -->
    <div class="row g-2 align-items-end mb-1">
      <div class="col-4">
        <input class="form-control" disabled type="text" value="AUDIO">
      </div>
      <div class="col-4">
        <input class="form-control" disabled placeholder="by stream type, not width" type="text">
      </div>
      <div class="col-2"></div>
    </div>
    <div formArrayName="drm_streamtypes">
      @for (tier of drmTiers.controls; track tier; let ti = $index) {
        <div [formGroupName]="ti" class="row g-2 align-items-end mb-1">
          <div class="col-4">
            <select class="form-select" formControlName="streamtype">
              @for (t of streamTypes; track t) {
                <option [ngValue]="t">{{ t }}</option>
              }
            </select>
          </div>
          <div class="col-4">
            @if (ti === drmTiers.length - 1) {
              <input class="form-control" disabled placeholder="unbounded" type="text">
            } @else {
              <input class="form-control" formControlName="max_width" min="1" step="1" type="number">
            }
          </div>
          <div class="col-2">
            <button
              (click)="removeDrmTier(ti)"
              [disabled]="drmTiers.length < 2"
              aria-label="Remove DRM tier"
              class="cl-btn-quickaction"
              type="button">✕
            </button>
          </div>
        </div>
      }
    </div>
    @if (drmTiers.errors?.tierOrder || drmTiers.errors?.duplicateTier) {
      <p class="invalid-feedback d-block">
        Declare tiers ascending and once each, in the order {{ streamTypes.join(', ') }}.
      </p>
    }
    @if (drmTiers.errors?.tierAscending || drmTiers.errors?.tierUnbounded) {
      <p class="invalid-feedback d-block">Every bounded tier needs a max width, and they must strictly ascend.</p>
    }
    @if (drmTiers.length < streamTypes.length) {
      <button (click)="addDrmTier()" class="btn btn-sm cl-color-secondary mt-2" type="button">Add tier</button>
    }
          </div>
        }

      </div>
    </div>
  </div>
</div>
